import { Link } from 'react-router-dom'
import styled, { css, keyframes } from 'styled-components'
import { Surface } from '../../components/DashboardPrimitives'
import { WINDOW_SIZE } from '../../util/Constants'

// Design language for the Send workspace: 8px spacing grid, minimal borders
// (background contrast instead), 14px base text, tabular numerals on every
// amount, and one subtle reveal transition on step unlock.

export const focusRing = css`
  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(49, 87, 198, 0.28);
    outline: none;
  }
`

const revealUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

/** Wraps content that appears when a step unlocks. Honors reduced motion. */
export const StepReveal = styled.div`
  animation: ${revealUp} 0.24s ease;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

export const SendLayout = styled.div`
  display: grid;
  gap: 16px;
  grid-template-columns: minmax(0, 760px) minmax(260px, 320px);
  justify-content: center;
  min-width: 0;
  width: 100%;

  @media (max-width: ${WINDOW_SIZE.TABLET}) {
    grid-template-columns: 1fr;
  }
`

export const CenteredWorkspace = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 8px auto 0;
  max-width: 1110px;
  width: 100%;
`

export const SendHero = styled.div`
  align-items: flex-end;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  max-width: 760px;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    align-items: flex-start;
    flex-direction: column;
  }
`

export const SendTitle = styled.h1`
  color: var(--ink, #20242d);
  font-size: 20px;
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1.2;
  margin: 0;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    font-size: 18px;
  }
`

export const UnderlineTabs = styled.div`
  align-items: flex-end;
  border-bottom: 1px solid var(--border, #e4e9f1);
  display: flex;
  gap: 20px;
  min-width: 0;
`

export const UnderlineTab = styled.button<{ $active?: boolean }>`
  background: transparent;
  border: 0;
  border-bottom: 2px solid ${({ $active }) => $active ? 'var(--ink, #20242d)' : 'transparent'};
  border-radius: 2px 2px 0 0;
  color: ${({ $active }) => $active ? 'var(--ink, #20242d)' : 'var(--text-muted, #6c7483)'};
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 13.5px;
  font-weight: 500;
  line-height: 1.2;
  margin-bottom: -1px;
  min-height: 34px;
  padding: 0 0 8px;
  transition: color 0.16s ease, border-color 0.16s ease;
  white-space: nowrap;

  &:hover {
    color: var(--ink, #20242d);
  }

  &:disabled {
    color: var(--text-disabled, #a4aab5);
    cursor: not-allowed;
  }

  ${focusRing}
`

export const FlowSurface = styled(Surface)`
  gap: 24px;
  padding: 24px;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    padding: 16px;
  }
`

export const SectionLabel = styled.span`
  color: var(--text-muted, #717887);
  font-size: 12.75px;
  font-weight: 410;
  line-height: 1.35;
`

export const SelectionMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`

export const SelectionLabel = styled.span`
  color: var(--ink, #20242d);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.25;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const SelectionSubtext = styled.span`
  color: var(--text-soft, #7d8492);
  font-size: 12.5px;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const SimpleMethodIcon = styled.div<{ $logo?: string, $size?: number }>`
  background-image: ${({ $logo }) => $logo ? `url(${$logo})` : 'none'};
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  flex: 0 0 auto;
  height: ${({ $size }) => $size ?? 22}px;
  width: ${({ $size }) => $size ?? 22}px;
`

/** Initials in a colored circle; hue derives from the recipient name hash. */
export const AvatarCircle = styled.div<{ $hue: number, $size?: number }>`
  align-items: center;
  background: ${({ $hue }) => `hsl(${$hue}, 58%, 90%)`};
  border-radius: 999px;
  color: ${({ $hue }) => `hsl(${$hue}, 45%, 30%)`};
  display: flex;
  flex: 0 0 auto;
  font-size: ${({ $size }) => Math.round(($size ?? 32) * 0.38)}px;
  font-weight: 600;
  height: ${({ $size }) => $size ?? 32}px;
  justify-content: center;
  letter-spacing: 0.02em;
  line-height: 1;
  user-select: none;
  width: ${({ $size }) => $size ?? 32}px;
`

export const RecentChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
`

export const RecentChip = styled.button`
  align-items: center;
  background: var(--surface-hover, #f4f6fa);
  border: 0;
  border-radius: 999px;
  color: var(--ink, #20242d);
  cursor: pointer;
  display: inline-flex;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  gap: 8px;
  line-height: 1.2;
  max-width: 200px;
  padding: 4px 12px 4px 4px;
  transition: background-color 0.16s ease;

  &:hover {
    background: #e9edf4;
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  ${focusRing}
`

/** Card shown once a recipient is chosen. */
export const SelectedRecipientCard = styled.div`
  align-items: center;
  background: var(--surface-muted, #f8fafc);
  border-radius: 14px;
  display: flex;
  gap: 12px;
  justify-content: space-between;
  min-width: 0;
  padding: 12px 16px;
`

// ---------------------------------------------------------------------------
// Hero amount entry
// ---------------------------------------------------------------------------

export const HeroAmountRow = styled.div`
  align-items: baseline;
  cursor: text;
  display: flex;
  gap: 8px;
  min-width: 0;
`

export const HeroAmountField = styled.input<{ $chars: number }>`
  background: transparent;
  border: 0;
  border-radius: 8px;
  caret-color: var(--ink, #20242d);
  color: var(--ink-strong, #121722);
  font-family: var(--font-sans);
  font-size: 40px;
  font-variant-numeric: tabular-nums;
  font-weight: 550;
  letter-spacing: -0.015em;
  line-height: 1.15;
  max-width: 100%;
  min-width: 0;
  outline: none;
  padding: 0;
  width: ${({ $chars }) => Math.max($chars, 1)}ch;

  &::placeholder {
    color: var(--border-strong, #c2c9d6);
  }

  &:disabled {
    color: var(--text-faint, #9aa3b2);
    cursor: not-allowed;
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(49, 87, 198, 0.28);
  }

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    font-size: 32px;
  }
`

export const HeroCurrencySuffix = styled.span`
  color: var(--text-faint, #8a919e);
  flex: 0 0 auto;
  font-size: 18px;
  font-weight: 500;
  line-height: 1.2;
`

export const HeroHelperRow = styled.div`
  align-items: center;
  color: var(--text-soft, #7d8492);
  display: flex;
  flex-wrap: wrap;
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;
  gap: 8px;
  line-height: 1.35;
  min-height: 20px;
`

export const MaxTextButton = styled.button`
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: var(--ink, #20242d);
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 12.5px;
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 3px;
  line-height: 1.35;
  padding: 0 2px;

  &:hover {
    text-decoration: underline;
  }

  &:disabled {
    color: var(--text-disabled, #a4aab5);
    cursor: not-allowed;
    text-decoration: none;
  }

  ${focusRing}
`

/** Lighter "Recipient gets | You pay" toggle. */
export const LightSegmented = styled.div`
  background: var(--surface-hover, #f4f6fa);
  border-radius: 999px;
  display: inline-flex;
  gap: 2px;
  padding: 2px;
  width: fit-content;
`

export const LightSegmentedButton = styled.button<{ $active?: boolean }>`
  background: ${({ $active }) => $active ? 'var(--surface, #ffffff)' : 'transparent'};
  border: 0;
  border-radius: 999px;
  box-shadow: ${({ $active }) => $active ? '0 1px 2px rgba(20, 28, 45, 0.08)' : 'none'};
  color: ${({ $active }) => $active ? 'var(--ink, #20242d)' : 'var(--text-muted, #697386)'};
  cursor: pointer;
  font-family: var(--font-sans);
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.2;
  min-height: 26px;
  padding: 0 12px;
  transition: background-color 0.16s ease, color 0.16s ease;

  &:hover {
    color: var(--ink, #20242d);
  }

  ${focusRing}
`

// ---------------------------------------------------------------------------
// Quote summary
// ---------------------------------------------------------------------------

/** Compact one-line quote under the amount, expandable to the full breakdown. */
export const QuoteLineButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 8px;
  color: var(--text-muted, #6b7280);
  cursor: pointer;
  display: flex;
  flex-wrap: wrap;
  font-family: var(--font-sans);
  font-size: 12.5px;
  font-variant-numeric: tabular-nums;
  gap: 6px;
  line-height: 1.4;
  padding: 0;
  text-align: left;
  width: fit-content;

  &:hover {
    color: var(--ink, #20242d);
  }

  ${focusRing}
`

export const QuoteBox = styled.div<{ $error?: boolean }>`
  background: ${({ $error }) => $error ? '#fff5f4' : 'var(--surface-muted, #f8fafc)'};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
`

export const QuoteRow = styled.div`
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  min-height: 22px;
`

export const QuoteLabel = styled.span`
  color: var(--text-muted, #6b7280);
  font-size: 12.5px;
`

export const QuoteValue = styled.span`
  color: var(--ink, #20242d);
  font-size: 12.75px;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  text-align: right;
`

export const InfoCard = styled.div`
  background: var(--surface-muted, #f8fafc);
  border-radius: 12px;
  color: var(--text-soft, #7d8492);
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 12.5px;
  line-height: 1.45;
  padding: 12px 16px;
`

export const BlockerNotice = styled.div`
  background: #fff8f2;
  border-radius: 12px;
  color: #8a5a24;
  font-size: 12.5px;
  line-height: 1.5;
  padding: 10px 14px;
`

// ---------------------------------------------------------------------------
// Review & success
// ---------------------------------------------------------------------------

export const ReviewIdentity = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0 0;
  text-align: center;
`

export const ReviewName = styled.span`
  color: var(--ink, #20242d);
  font-size: 15px;
  font-weight: 500;
  line-height: 1.3;
  overflow-wrap: anywhere;
`

export const ReviewDetail = styled.span`
  color: var(--text-soft, #7d8492);
  font-size: 12.5px;
  line-height: 1.4;
  overflow-wrap: anywhere;
`

export const ReviewAmount = styled.div`
  color: var(--ink-strong, #121722);
  font-size: 34px;
  font-variant-numeric: tabular-nums;
  font-weight: 550;
  letter-spacing: -0.015em;
  line-height: 1.15;
  padding: 8px 0;
  text-align: center;

  @media (max-width: ${WINDOW_SIZE.MOBILE_LARGE}) {
    font-size: 28px;
  }
`

export const BackTextButton = styled.button`
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 8px;
  color: var(--text-muted, #6c7483);
  cursor: pointer;
  display: inline-flex;
  font-family: var(--font-sans);
  font-size: 13px;
  font-weight: 500;
  gap: 6px;
  line-height: 1.2;
  padding: 4px 6px;
  transition: color 0.16s ease;
  width: fit-content;

  &:hover {
    color: var(--ink, #20242d);
  }

  ${focusRing}
`

export const SuccessPanel = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 32px 16px 16px;
  text-align: center;
`

export const SuccessBadge = styled.div`
  align-items: center;
  background: #e8f7ef;
  border-radius: 999px;
  color: #16724b;
  display: flex;
  height: 56px;
  justify-content: center;
  width: 56px;
`

export const SuccessHeading = styled.h2`
  color: var(--ink, #20242d);
  font-size: 20px;
  font-weight: 550;
  line-height: 1.25;
  margin: 8px 0 0;
`

export const SuccessSummary = styled.p`
  color: var(--ink, #20242d);
  font-size: 14px;
  font-variant-numeric: tabular-nums;
  line-height: 1.45;
  margin: 0;
`

export const SuccessNote = styled.p`
  color: var(--text-soft, #7d8492);
  font-size: 12.5px;
  line-height: 1.5;
  margin: 0;
  max-width: 380px;
`

// Recent-sends rows in the Funding page's list style: icon + title/meta on
// the left, status and time on the right, hairline separators between rows.
export const ActivityRowLink = styled(Link)`
  align-items: center;
  background: var(--surface, #ffffff);
  border-radius: 0;
  color: inherit;
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(0, 1fr) auto;
  min-height: 54px;
  padding: 10px 8px;
  text-decoration: none;

  & + & {
    border-top: 1px solid var(--hairline-soft, #edf1f7);
  }

  &:hover {
    background: var(--surface-subtle, #fbfcfe);
  }

  &:focus-visible {
    outline: 2px solid var(--ink, #20242d);
    outline-offset: -2px;
  }
`

// Visible entry points for saving a destination, so account creation isn't
// discoverable only from inside the picker dropdown.
export const QuickActionsRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

export const QuickActionPill = styled.button`
  align-items: center;
  background: var(--surface, #ffffff);
  border: 1px solid var(--border, #e3e7ef);
  border-radius: 999px;
  color: var(--text-muted, #384252);
  cursor: pointer;
  display: inline-flex;
  font-size: 12.5px;
  font-weight: 500;
  gap: 6px;
  line-height: 1;
  padding: 8px 13px;
  transition: background-color 0.16s ease, border-color 0.16s ease, color 0.16s ease, transform 0.1s ease;

  &:hover {
    background: var(--surface-muted, #f8fafc);
    border-color: var(--border-strong, #d5dbe6);
    color: var(--ink, #20242d);
  }

  &:active {
    transform: translateY(1px) scale(0.99);
  }

  &:focus-visible {
    outline: 2px solid var(--ink, #20242d);
    outline-offset: 2px;
  }
`

export const QuickActionGlyph = styled.span`
  color: var(--text-faint, #8a919e);
  font-size: 13px;
  line-height: 1;
  transition: inherit;

  ${QuickActionPill}:hover & {
    color: var(--ink, #20242d);
  }
`

// Inline card for saving a pasted one-off IBAN as a recipient (customer +
// bank account) without leaving the send flow.
export const RecipientCard = styled.div`
  background: var(--surface-subtle, #fcfcfd);
  border: 1px solid var(--border-soft, #eceff3);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 16px;
`

export const RecipientCardTitle = styled.div`
  color: var(--ink, #20242d);
  font-size: 14.5px;
  font-weight: 500;
`

export const RecipientCardHint = styled.div`
  color: var(--text-muted, #6b7280);
  font-size: 12.5px;
  line-height: 1.5;
`

export const RecipientFieldsGrid = styled.div`
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`
